import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  Output,
  OnInit,
} from '@angular/core';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import { CheckboxComponent } from '@shared/components/forms/checkbox/checkbox.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TuiButtonModule, TuiHintModule } from '@taiga-ui/core';
import { TransitPointService } from '@shared/services/transit-point.service';
import { AsyncPipe, UpperCasePipe } from '@angular/common';
import {
  BehaviorSubject,
  map,
  Subject,
  tap,
  withLatestFrom,
  combineLatest,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TuiActiveZoneModule } from '@taiga-ui/cdk';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { getLetterByIndex } from '@shared/utils/common';
import { Store } from '@ngxs/store';
import { CargoTransitPointForm } from '../../../../../../types/cargo';
import { CargoLoadingState } from '../../../../store/cargo-loading.state';

@Component({
  selector: 'app-transit-point-form',
  standalone: true,
  imports: [
    AutocompleteComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    TuiHintModule,
    TuiButtonModule,
    AsyncPipe,
    TuiActiveZoneModule,
    CdkDrag,
    UpperCasePipe,
  ],
  templateUrl: './transit-point-form.component.html',
  styleUrl: './transit-point-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransitPointFormComponent implements OnInit {
  @Input({ required: true })
  transitPointForm!: FormGroup<CargoTransitPointForm>;

  @Input({ required: true })
  canRemove: boolean;

  index$ = new BehaviorSubject(0);
  @Output() remove = new EventEmitter();
  transitPoints$ = this.transitPointService.getTransitPoints();
  transitPointsMap$ = this.transitPointService.getTransitPointsMap();
  fillTransitPointDetails$ = new Subject<Nullable<string>>();
  letter$ = combineLatest([
    this.store.select(CargoLoadingState.getSelectedCargo),
    this.index$,
  ]).pipe(
    map(([cargo, index]) => {
      const offset = cargo?.loadingAddressesJson.length ?? 0;
      return getLetterByIndex(index + offset);
    }),
  );

  constructor(
    private readonly transitPointService: TransitPointService,
    private readonly destroyRef: DestroyRef,
    private readonly store: Store,
  ) {}

  onFillTransitPointDetails() {
    this.fillTransitPointDetails$
      .pipe(
        withLatestFrom(this.transitPointsMap$),
        tap(([id, transitPointsMap]) => {
          const group = this.transitPointForm.controls.transitPoint;
          if (!id) {
            group.reset();
          } else {
            const point = transitPointsMap.get(Number(id));
            if (point) {
              group.setValue(point);
            } else {
              group.reset();
            }
          }
          group.markAsTouched();
          group.markAsDirty();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @Input({ required: true })
  set index(value: number) {
    this.index$.next(value);
  }

  ngOnInit() {
    this.onFillTransitPointDetails();
  }
}
